import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Printer, Ban, Search, Plus } from 'lucide-react';
import PreauthCodeDialog from '../../components/PreauthCodeDialog';
import { formatCATDateTime } from '../../utils/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CLS = { issued: 'bg-emerald-100 text-emerald-800', used: 'bg-gray-200 text-gray-700', cancelled: 'bg-red-100 text-red-800', expired: 'bg-amber-100 text-amber-900' };

// Acacia office register of pre-authorization codes: issue, search, print, cancel.
export default function PreauthCodesRegister({ authCfg, canEdit, setDialog }) {
  const [codes, setCodes] = useState([]);
  const [status, setStatus] = useState('issued');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [issueFor, setIssueFor] = useState(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberHits, setMemberHits] = useState([]);

  useEffect(() => { const t = setTimeout(() => setQ(search.trim()), 350); return () => clearTimeout(t); }, [search]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/preauth-codes`, { params: { status: status === 'all' ? undefined : status, q: q || undefined }, ...authCfg });
      setCodes(res.data.codes || []);
    } catch (e) { /* non-critical */ }
  }, [status, q, authCfg]);
  useEffect(() => { load(); }, [load]);

  const findMember = async () => {
    if (!memberQuery.trim()) return;
    try {
      const res = await axios.get(`${API}/patients/search`, { params: { query: memberQuery.trim() }, ...authCfg });
      const raw = res.data.type === 'individual' ? (res.data.results || []) : (res.data.members || []);
      const hits = raw.map((m) => ({ ...m, family_id: m.family_id || res.data.family?.family_id }));
      setMemberHits(hits);
      if (hits.length === 0) toast.info('No member found for that ID or name');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Member search failed');
    }
  };

  const cancel = (c) => setDialog({
    title: 'Cancel pre-authorization code',
    description: `Cancel code ${c.code} for ${c.patient_name} at ${c.hospital_name}? The hospital will no longer be able to check the patient in with it.`,
    confirmLabel: 'Cancel Code', destructive: true,
    onConfirm: async () => {
      try { await axios.post(`${API}/preauth-codes/${c.code}/cancel`, {}, authCfg); toast.success(`Code ${c.code} cancelled`); load(); }
      catch (e) { toast.error(e.response?.data?.detail || 'Could not cancel'); }
    },
  });

  return (
    <div className="space-y-3" data-testid="preauth-codes-register">
      {canEdit && (
        <div className="bg-white rounded-xl border border-amber-200 p-3" data-testid="preauth-issue-box">
          <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-1"><Plus className="w-4 h-4" /> Issue a new pre-authorization code</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && findMember()} placeholder="Member serial (GS-1234-01), family ID or name" className="h-9" data-testid="preauth-issue-search" />
            <Button onClick={findMember} variant="outline" className="h-9 border-amber-300 text-amber-800 hover:bg-amber-50" data-testid="preauth-issue-find"><Search className="w-4 h-4 mr-1" /> Find member</Button>
          </div>
          {memberHits.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {memberHits.map((m) => (
                <button key={m.serial_number} onClick={() => { setIssueFor(m); setMemberHits([]); }} className="px-3 py-1.5 rounded-full border border-amber-300 bg-amber-50 text-xs text-amber-900 hover:bg-amber-100" data-testid={`preauth-issue-member-${m.serial_number}`}>
                  {[m.first_name, m.last_name].filter(Boolean).join(' ')} • {m.serial_number}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-3 border-b border-gray-200 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search code, member, family or hospital" className="pl-9 h-9" data-testid="preauth-codes-search" />
          </div>
          <div className="flex gap-1">
            {[['issued', 'Active'], ['used', 'Used'], ['expired', 'Expired'], ['cancelled', 'Cancelled'], ['all', 'All']].map(([k, label]) => (
              <button key={k} onClick={() => setStatus(k)} className={`px-3 h-9 rounded-md text-xs font-medium border ${status === k ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`} data-testid={`preauth-codes-filter-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        {codes.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500" data-testid="preauth-codes-empty">No pre-authorization codes {status === 'all' ? '' : `(${status})`}.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="preauth-codes-table">
              <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                <tr><th className="px-3 py-2">Code</th><th className="px-3 py-2">Member</th><th className="px-3 py-2">Hospital</th><th className="px-3 py-2">Reason</th><th className="px-3 py-2">Issued / Expires</th><th className="px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.code} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`preauth-code-row-${c.code}`}>
                    <td className="px-3 py-2 font-mono font-bold text-amber-900">{c.code}</td>
                    <td className="px-3 py-2"><div className="font-medium text-gray-900">{c.patient_name}</div><div className="text-xs text-gray-500 font-mono">{c.patient_serial_number} • {c.family_id}</div></td>
                    <td className="px-3 py-2 text-gray-800">{c.hospital_name}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[220px] truncate" title={c.reason}>{c.reason}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{formatCATDateTime(c.issued_at)}<br />exp. {new Date(c.expires_at).toLocaleDateString('en-GB')}{c.used_visit_id ? <><br />used: {c.used_visit_id}</> : null}</td>
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[c.status] || ''}`}>{c.status}</span></td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => window.open(`/print-preauth/${c.code}`, '_blank')} className="p-1.5 text-amber-700 hover:bg-amber-50 rounded" title="Print form" data-testid={`preauth-code-print-${c.code}`}><Printer className="w-4 h-4" /></button>
                      {canEdit && c.status === 'issued' && <button onClick={() => cancel(c)} className="p-1.5 text-red-600 hover:bg-red-50 rounded" title="Cancel code" data-testid={`preauth-code-cancel-${c.code}`}><Ban className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <PreauthCodeDialog open={!!issueFor} onOpenChange={(o) => { if (!o) setIssueFor(null); }} member={issueFor} onIssued={load} />
    </div>
  );
}
